import { useCallback } from 'react'
import { useMutation } from '@apollo/client/react'
import { AdminAPI, getFragment, SharedAPI, utils } from '@guestgoodie/graphql'

import * as Form from '@/features/form'
import { useLingui } from '@lingui/react/macro'

export type FormData = {
  identifier: string
  name: string
  description: string
}

interface Options {
  id?: number
  onDone?: () => void
}

export default function useSpaceForm(options: Options) {
  const { id, onDone } = options

  const { t } = useLingui()

  const [createSpace] = useMutation(AdminAPI.space.CREATE, {
    refetchQueries: [AdminAPI.space.QUERY]
  })

  const [updateSpace] = useMutation(AdminAPI.space.UPDATE, {
    refetchQueries: [
      {
        query: AdminAPI.space.GET,
        variables: {
          id
        }
      }
    ]
  })

  const [deleteSpace] = useMutation(AdminAPI.space.DELETE, {
    update(cache, { data }) {
      if (!data) return

      const space = getFragment(
        SharedAPI.space.SPACE_FRAGMENT,
        data.deleteSpace
      )

      utils.paginationHelpers.remove(cache, space.id, 'Space', 'querySpaces')
    }
  })

  const { errors, handlePromiseCatch } = Form.useErrorHelpers<FormData>({
    mapper(field, message) {
      if (field === 'identifier' && message === 'has already been taken') {
        return [
          field,
          {
            type: message,
            message: t`This identifier exists already.`
          }
        ]
      }
    }
  })

  const create = useCallback(
    (data: FormData) => {
      return createSpace({
        variables: {
          input: data
        }
      })
        .then(({ data, error }) => {
          if (error) return Promise.reject(error)
          if (!data) return Promise.reject('unknown')

          onDone?.()
        })
        .catch(error =>
          handlePromiseCatch(error, {
            customErrors: {
              not_enough_licenses: {
                key: 'error-not-enough-licenses',
                message: t({
                  comment:
                    'Error message shown when a user tries to create a room, even though it would succeed their license.',
                  message:
                    'This Room would exceed your current licenses, please update your licenses before continuing.'
                })
              }
            }
          })
        )
    },
    [onDone, createSpace, handlePromiseCatch, t]
  )

  const update = useCallback(
    async (data: FormData) => {
      if (!id) return

      return updateSpace({
        variables: {
          input: {
            id,
            ...data
          }
        }
      })
        .then(({ data, error }) => {
          if (error) return Promise.reject(error)
          if (!data) return Promise.reject('unknown')

          onDone?.()
        })
        .catch(error => handlePromiseCatch(error))
    },
    [id, onDone, updateSpace, handlePromiseCatch]
  )

  const remove = useCallback(() => {
    if (!id) return

    deleteSpace({ variables: { id } })
    onDone?.()
  }, [id, onDone, deleteSpace])

  return {
    errors,
    create,
    update,
    remove
  }
}
